import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';

export interface VestingCategory {
  id?: string;
  category: string;
  walletAddress: string;
  cliffPeriod: string;        // Changed to string to support "12 months", "1 months", etc.
  vestingDuration: string;    // Renamed from vestingPeriod
  releasingFrequency: string;
  tgeUnlock: number;          // Renamed from initialUnlock
  firstClaim: string;         // New field
}

@Component({
  selector: 'app-vesting-category-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    MessageModule
  ],
  templateUrl: './vesting-category-dialog.component.html',
  styleUrl: './vesting-category-dialog.component.css'
})
export class VestingCategoryDialogComponent implements OnInit {
  isEdit: boolean = false;

  // Form controls
  categoryControl = new FormControl('', [Validators.required, Validators.minLength(2)]);
  walletAddressControl = new FormControl('', [Validators.required, Validators.pattern(/^0x[a-fA-F0-9]{40}$/)]);
  cliffPeriodValueControl = new FormControl(0, [Validators.required, Validators.min(0)]);
  cliffPeriodUnitControl = new FormControl('months', [Validators.required]);
  vestingDurationValueControl = new FormControl(1, [Validators.required, Validators.min(1)]);
  vestingDurationUnitControl = new FormControl('months', [Validators.required]);
  releasingFrequencyControl = new FormControl('', [Validators.required, Validators.minLength(2)]);
  tgeUnlockControl = new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]);
  firstClaimValueControl = new FormControl(1, [Validators.required, Validators.min(0)]);
  firstClaimUnitControl = new FormControl('months', [Validators.required]);
  firstClaimTimingControl = new FormControl('after TGE', [Validators.required]);

  vestingCategoryForm: FormGroup;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private translateService: TranslateService
  ) {
    this.vestingCategoryForm = new FormGroup({
      category: this.categoryControl,
      walletAddress: this.walletAddressControl,
      cliffPeriodValue: this.cliffPeriodValueControl,
      cliffPeriodUnit: this.cliffPeriodUnitControl,
      vestingDurationValue: this.vestingDurationValueControl,
      vestingDurationUnit: this.vestingDurationUnitControl,
      releasingFrequency: this.releasingFrequencyControl,
      tgeUnlock: this.tgeUnlockControl,
      firstClaimValue: this.firstClaimValueControl,
      firstClaimUnit: this.firstClaimUnitControl,
      firstClaimTiming: this.firstClaimTimingControl
    });
  }

  ngOnInit(): void {
    // Check if we're editing an existing vesting category
    if (this.config.data?.vestingCategory) {
      this.isEdit = true;
      const vestingCategory: VestingCategory = this.config.data.vestingCategory;
      
      this.categoryControl.setValue(vestingCategory.category);
      this.walletAddressControl.setValue(vestingCategory.walletAddress);
      
      // Parse cliff period (e.g., "12 months" -> 12, "months")
      const cliffParts = vestingCategory.cliffPeriod.split(' ');
      this.cliffPeriodValueControl.setValue(parseInt(cliffParts[0]));
      this.cliffPeriodUnitControl.setValue(cliffParts[1] || 'months');
      
      // Parse vesting duration
      const vestingParts = vestingCategory.vestingDuration.split(' ');
      this.vestingDurationValueControl.setValue(parseInt(vestingParts[0]));
      this.vestingDurationUnitControl.setValue(vestingParts[1] || 'months');
      
      this.releasingFrequencyControl.setValue(vestingCategory.releasingFrequency);
      this.tgeUnlockControl.setValue(vestingCategory.tgeUnlock);
      
      // Parse first claim (e.g., "12 months after TGE" -> 12, "months", "after TGE")
      const firstClaimMatch = vestingCategory.firstClaim.match(/(\d+)\s+(\w+)\s+(.+)/);
      if (firstClaimMatch) {
        this.firstClaimValueControl.setValue(parseInt(firstClaimMatch[1]));
        this.firstClaimUnitControl.setValue(firstClaimMatch[2]);
        this.firstClaimTimingControl.setValue(firstClaimMatch[3]);
      }
    }
  }

  private markFormGroupTouched(): void {
    Object.values(this.vestingCategoryForm.controls).forEach(control => {
      control.markAsTouched();
    });
  }

  onSubmit(): void {
    if (this.vestingCategoryForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    const vestingCategoryData: VestingCategory = {
      category: this.categoryControl.value!,
      walletAddress: this.walletAddressControl.value!,
      cliffPeriod: `${this.cliffPeriodValueControl.value} ${this.cliffPeriodUnitControl.value}`,
      vestingDuration: `${this.vestingDurationValueControl.value} ${this.vestingDurationUnitControl.value}`,
      releasingFrequency: this.releasingFrequencyControl.value!,
      tgeUnlock: this.tgeUnlockControl.value!,
      firstClaim: `${this.firstClaimValueControl.value} ${this.firstClaimUnitControl.value} ${this.firstClaimTimingControl.value}`
    };

    if (this.isEdit && this.config.data?.vestingCategory?.id) {
      vestingCategoryData.id = this.config.data.vestingCategory.id;
    }

    this.ref.close(vestingCategoryData);
  }

  onCancel(): void {
    this.ref.close();
  }

  // Error message methods
  getCategoryErrorMessage(): string {
    if (this.categoryControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.vestingCategoryRequired');
    }
    if (this.categoryControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.vestingCategoryMinLength');
    }
    return '';
  }

  getWalletAddressErrorMessage(): string {
    if (this.walletAddressControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.vestingCategories.walletAddressRequired');
    }
    if (this.walletAddressControl.errors?.['pattern']) {
      return this.translateService.instant('projectEdit.vestingCategories.walletAddressInvalid');
    }
    return '';
  }

  getCliffPeriodErrorMessage(): string {
    if (this.cliffPeriodValueControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.cliffPeriodRequired');
    }
    if (this.cliffPeriodValueControl.errors?.['min']) {
      return this.translateService.instant('projectEdit.errors.cliffPeriodMin');
    }
    return '';
  }

  getVestingDurationErrorMessage(): string {
    if (this.vestingDurationValueControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.vestingPeriodRequired');
    }
    if (this.vestingDurationValueControl.errors?.['min']) {
      return this.translateService.instant('projectEdit.errors.vestingPeriodMin');
    }
    return '';
  }

  getReleasingFrequencyErrorMessage(): string {
    if (this.releasingFrequencyControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.releasingFrequencyRequired');
    }
    if (this.releasingFrequencyControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.releasingFrequencyMinLength');
    }
    return '';
  }

  getTgeUnlockErrorMessage(): string {
    if (this.tgeUnlockControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.initialUnlockRequired');
    }
    if (this.tgeUnlockControl.errors?.['min']) {
      return this.translateService.instant('projectEdit.errors.initialUnlockMin');
    }
    if (this.tgeUnlockControl.errors?.['max']) {
      return this.translateService.instant('projectEdit.errors.initialUnlockMax');
    }
    return '';
  }

  getFirstClaimErrorMessage(): string {
    if (this.firstClaimValueControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.vestingCategories.firstClaimRequired');
    }
    if (this.firstClaimValueControl.errors?.['min']) {
      return this.translateService.instant('projectEdit.vestingCategories.firstClaimMin');
    }
    return '';
  }
}